import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import type { ProjectWithRelations } from '../lib/useUserProjects';
import type { Category } from '../lib/database.client.types';
import { getRelativeTime } from '../utils/dateHelpers';
import { useFontLoading } from '../lib/FontContext';
import { interSemiBold, interMedium, interRegular } from "../lib/fonts";
import colors from '../lib/colors';
import { isProjectActivated } from '../lib/projectActivation';
import { buildDashboardPath } from '../lib/dashboardRoutes';
import { PaperFeedButton, ProjectSettingsButton } from './ui/Button';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface ProjectCardProps {
    project: ProjectWithRelations;
    legacyTransitionPaused?: boolean;
}

function ProjectCard({ project, legacyTransitionPaused = false }: ProjectCardProps) {
    const { i18n } = useLingui();
    const router = useRouter();
    const pathname = usePathname();
    const { fontsLoaded } = useFontLoading();

    // Extract categories
    const categories = useMemo(() => {
        if (!project.project_category_interests) return [];
        return project.project_category_interests
            .map(pci => pci.category)
            .filter(Boolean) as Category[];
    }, [project.project_category_interests]);

    // Get displayed categories (max 3)
    const displayedCategories = categories.slice(0, 3);
    const remainingCount = categories.length - 3;

    // Count keywords
    const keywordCount = project.project_keywords?.length || 0;

    // Determine status based on activation and paused state
    const isPaused = !!(project as any).paused_at;
    const isActive = isProjectActivated(project);
    const needsSetup = !isActive;
    const statusText = isPaused
        ? i18n._(msg({ id: 'project.status.paused', message: 'Paused' }))
        : isActive
            ? i18n._(msg({ id: 'project.status.active', message: 'Active' }))
            : i18n._(msg({ id: 'project.status.setupNeeded', message: 'Setup needed' }));
    const statusColor = isPaused ? '#d97706' : isActive ? colors.primaryGreen : colors.text500;

    // Determine visibility
    const isPublic = project.visibility === 'public';
    const visibilityText = isPublic
        ? i18n._(msg({ id: 'project.visibility.public', message: 'Public' }))
        : project.visibility === 'unlisted'
            ? i18n._(msg({ id: 'project.visibility.unlisted', message: 'Unlisted' }))
            : i18n._(msg({ id: 'project.visibility.private', message: 'Private' }));
    const visibilityColor = isPublic ? colors.action : colors.text500;

    // Get relative time
    const createdTime = project.created_at ? getRelativeTime(i18n, project.created_at) : '';

    const handleOpenSettings = () => {
        router.push(buildDashboardPath(
            pathname,
            needsSetup ? `/${project.id}/activate` : `/${project.id}/settings`
        ));
    };

    const handleOpenFeed = () => {
        router.push(buildDashboardPath(
            pathname,
            needsSetup ? `/${project.id}/activate` : `/${project.id}`
        ));
    };

    return (
        <View style={styles.card} testID={`dashboard-project-card-${project.id}`}>
            {/* Header: Name + Badges */}
            <View style={styles.header}>
                <Text style={[styles.name, fontsLoaded && interSemiBold]} numberOfLines={1}>
                    {project.name || <Trans id="projectcard.untitledProject" comment="Conditional UI copy in components/ProjectCard.tsx">Untitled Project</Trans>}
                </Text>
                <View style={styles.badgeContainer}>
                    <View style={[styles.badge, { borderColor: statusColor }]}>
                        <Text style={[styles.badgeText, { color: statusColor }, fontsLoaded && interSemiBold]}>
                            {statusText}
                        </Text>
                    </View>
                    <View style={[styles.badge, { borderColor: visibilityColor }]}>
                        <Text style={[styles.badgeText, { color: visibilityColor }, fontsLoaded && interSemiBold]}>
                            {visibilityText}
                        </Text>
                    </View>
                </View>
            </View>

            {/* Categories */}
            {displayedCategories.length > 0 && (
                <View style={styles.categoriesRow}>
                    {displayedCategories.map((category, index) => (
                        <View key={category.id} style={styles.categoryChip}>
                            <Text style={[styles.categoryText, fontsLoaded && interRegular]} numberOfLines={1}>
                                {category.name || category.code}
                            </Text>
                        </View>
                    ))}
                    {remainingCount > 0 && (
                        <View style={styles.categoryChip}>
                            <Text style={[styles.categoryText, fontsLoaded && interRegular]}><Trans id="projectcard.more" comment="UI copy in components/ProjectCard.tsx">
                                +{remainingCount} more
                            </Trans></Text>
                        </View>
                    )}
                </View>
            )}

            {/* Description */}
            {project.interest_description && (
                <Text style={[styles.description, fontsLoaded && interRegular]} numberOfLines={2}>
                    {project.interest_description}
                </Text>
            )}

            {/* Metadata row */}
            <View style={styles.metadataRow}>
                {createdTime && (
                    <Text style={[styles.metadata, fontsLoaded && interRegular]}><Trans id="projectcard.created" comment="UI copy in components/ProjectCard.tsx">
                        Created {createdTime}
                    </Trans></Text>
                )}
                {project.max_papers_per_digests && (
                    <Text style={[styles.metadata, fontsLoaded && interRegular]}><Trans id="projectcard.maxPapers" comment="UI copy in components/ProjectCard.tsx">
                        • Max {project.max_papers_per_digests} papers
                    </Trans></Text>
                )}
                {keywordCount > 0 && (
                    <Text style={[styles.metadata, fontsLoaded && interRegular]}>
                        • {keywordCount} {keywordCount === 1 ? <Trans id="projectcard.keyword" comment="Conditional UI copy in components/ProjectCard.tsx">keyword</Trans> : <Trans id="projectcard.keywords" comment="Conditional UI copy in components/ProjectCard.tsx">keywords</Trans>}
                    </Text>
                )}
                {needsSetup && (
                    <Text style={[styles.metadata, styles.metadataEmphasis, fontsLoaded && interMedium]}><Trans id="projectcard.continueSetup" comment="UI copy in components/ProjectCard.tsx">
                        • Continue setup
                    </Trans></Text>
                )}
            </View>

            {legacyTransitionPaused && isPaused && (
                <Text style={[styles.legacyPauseNote, fontsLoaded && interRegular]}><Trans id="projectcard.pausedDuringTheFreePlanTransitionPrivacyHistoryAndSettingsAre" comment="UI copy in components/ProjectCard.tsx">
                    Paused during the Free-plan transition. Privacy, history, and settings are preserved. Resume it from Settings when your plan has capacity.
                </Trans></Text>
            )}

            <View style={styles.actionsRow}>
                <ProjectSettingsButton
                    variant="secondary"
                    style={styles.actionButton}
                    textStyle={styles.actionButtonText}
                    onPress={handleOpenSettings}
                    testID={`dashboard-project-card-settings-${project.id}`}
                />

                <PaperFeedButton
                    variant="primary"
                    style={styles.actionButton}
                    textStyle={styles.actionButtonText}
                    onPress={handleOpenFeed}
                    testID={`dashboard-project-card-feed-${project.id}`}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: colors.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        marginBottom: 12,
        padding: 16,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
        gap: 8,
    },
    name: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text900,
        flex: 1,
    },
    badgeContainer: {
        flexDirection: 'row',
        gap: 6,
    },
    badge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: '#ffffff',
        borderWidth: 1,
    },
    badgeText: {
        fontSize: 11,
        fontWeight: '600',
    },
    categoriesRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 8,
    },
    categoryChip: {
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#d1d5db',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        maxWidth: 150,
    },
    categoryText: {
        fontSize: 11,
        color: colors.text700,
    },
    description: {
        fontSize: 14,
        color: colors.text700,
        lineHeight: 20,
        marginBottom: 8,
    },
    metadataRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 4,
    },
    metadata: {
        fontSize: 12,
        color: colors.text500,
    },
    metadataEmphasis: {
        color: colors.primaryGreenDark,
    },
    legacyPauseNote: {
        color: '#a16207',
        fontSize: 12,
        lineHeight: 18,
        marginTop: 8,
    },
    actionsRow: {
        marginTop: 16,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 10,
    },
    actionButton: {
        minHeight: 40,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 10,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOpacity: 0.08,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
        }),
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
        }),
    },
    actionButtonText: {
        fontSize: 13,
    },
});

// Memoize to prevent unnecessary re-renders when other projects in the list update
export default React.memo(ProjectCard);
